import { useState, useEffect } from 'react';

export default function Home() {
  const [activeService, setActiveService] = useState(0);
  const [activePricing, setActivePricing] = useState(1);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // Add Schema.org JSON-LD
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "StarSales B.V.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
      "logo": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/logo.png`,
      "description": "Websites, Google Ads en social media advertenties voor ZZP en kleine bedrijven",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Mgr. Cornelis Veermanlaan 63",
        "addressLocality": "Volendam",
        "postalCode": "1131 KE",
        "addressCountry": "NL"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+31-970-102-241-37",
        "contactType": "customer service",
        "availableLanguage": "Dutch"
      },
      "sameAs": [
        "https://www.facebook.com/starsales",
        "https://www.instagram.com/starsales",
        "https://www.linkedin.com/company/starsales"
      ],
      "founder": {
        "@type": "Person",
        "name": "Daan de Jong"
      },
      "numberOfEmployees": "5",
      "foundingDate": "2019",
      "areaServed": "Netherlands",
      "serviceType": [
        "Website Development",
        "Google Ads Management",
        "Social Media Advertising",
        "SEO Services",
        "Web Hosting"
      ]
    });
    document.head.appendChild(script);

    // Add LocalBusiness Schema
    const localBusinessScript = document.createElement('script');
    localBusinessScript.type = 'application/ld+json';
    localBusinessScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "StarSales B.V.",
      "image": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/logo.png`,
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
      "telephone": "+31-970-102-241-37",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Mgr. Cornelis Veermanlaan 63",
        "addressLocality": "Volendam",
        "postalCode": "1131 KE",
        "addressCountry": "NL"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 52.4987654,
        "longitude": 5.0123456
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday", 
          "Wednesday",
          "Thursday",
          "Friday"
        ],
        "opens": "09:00",
        "closes": "17:00"
      },
      "priceRange": "€€"
    });
    document.head.appendChild(localBusinessScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(localBusinessScript);
    };
  }, []);

  const services = [
    {
      title: "Websites",
      description: "Snel, modern, SEO-basis, AVG-proof",
      icon: "ri-computer-line"
    },
    {
      title: "Google Ads",
      description: "Zoek/display, tracking, A/B-test",
      icon: "ri-google-line"
    },
    {
      title: "Social Ads",
      description: "FB/IG/TikTok, retargeting",
      icon: "ri-facebook-line"
    },
    {
      title: "Google Maps",
      description: "Profieloptimalisatie, reviews",
      icon: "ri-map-pin-line"
    },
    {
      title: "SEO & Content",
      description: "Techniek, lokaal, blogs",
      icon: "ri-search-line"
    },
    {
      title: "Hosting & Onderhoud",
      description: "Updates, back-ups",
      icon: "ri-server-line"
    }
  ];

  const benefits = [
    {
      title: "Specialist in ZZP & MKB",
      icon: "ri-user-star-line"
    },
    {
      title: "Snel live, helder geprijsd",
      icon: "ri-time-line"
    },
    {
      title: "Meetbaar resultaat",
      icon: "ri-bar-chart-line"
    },
    {
      title: "Google Ads & Meta expertise",
      icon: "ri-advertisement-line"
    },
    {
      title: "Hosting & support in NL",
      icon: "ri-shield-check-line"
    }
  ];

  const processSteps = [
    { title: "Intake", description: "We bespreken jouw doelen en wensen" },
    { title: "Design", description: "We ontwerpen een op maat gemaakte oplossing" },
    { title: "Bouw & tracking", description: "We bouwen en implementeren alles" },
    { title: "Lancering & groei", description: "We lanceren en optimaliseren continu" }
  ];

  const pricingPlans = [
    {
      name: "Starter ZZP",
      description: "Simpele site",
      price: "€249",
      features: ["Responsive website", "SEO basis", "Contactformulier", "Google Analytics", "6 maanden support"]
    },
    {
      name: "Groei",
      description: "Site + Ads + rapportage",
      price: "€1.299",
      features: ["Alles van Starter", "Google Ads setup", "Maandelijkse rapportage", "A/B testing", "6 maanden support"],
      popular: true
    },
    {
      name: "Pro",
      description: "Maatwerk + SEO + multi-channel",
      price: "Op maat",
      features: ["Alles van Groei", "Social media ads", "Uitgebreide SEO", "Maandelijks overleg", "6 maanden support"]
    }
  ];

  const faqs = [
    {
      question: "Hoe lang duurt het voordat mijn website live is?",
      answer: "Een standaard website is binnen 2-3 werkdagen live. Voor maatwerk kan dit 4-6 werkdagen duren, afhankelijk van de complexiteit."
    },
    {
      question: "Kunnen jullie mijn bestaande website verbeteren?",
      answer: "Ja, we kunnen bestaande websites optimaliseren voor betere prestaties, SEO en conversie. We maken eerst een analyse van de huidige situatie."
    },
    {
      question: "Wat is de minimale looptijd voor advertentiecampagnes?",
      answer: "We adviseren minimaal 3 maanden voor Google Ads en social media campagnes om voldoende data te verzamelen en te optimaliseren."
    },
    {
      question: "Hoe meten jullie het resultaat van campagnes?",
      answer: "We gebruiken uitgebreide tracking via Google Analytics, Google Ads en Facebook Pixel. Maandelijks ontvang je een duidelijk rapport met alle belangrijke cijfers."
    },
    {
      question: "In welk werkgebied zijn jullie actief?",
      answer: "We werken voornamelijk digitaal, dus kunnen klanten door heel Nederland bedienen. Voor persoonlijke gesprekken komen we graag naar de regio Noord-Holland."
    },
    {
      question: "Wat voor support krijg ik na oplevering?",
      answer: "Alle pakketten bevatten support voor technische vragen en kleine aanpassingen. Voor uitgebreidere wijzigingen maken we een offerte op maat."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <span className="text-2xl font-bold text-[#0A66C2]" style={{ fontFamily: '"Pacifico", serif' }}>
                StarSales
              </span>
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="/" className="text-gray-900 hover:text-[#0A66C2] font-medium transition-colors">Home</a>
              <a href="/over-ons" className="text-gray-700 hover:text-[#0A66C2] font-medium transition-colors">Over ons</a>
              <a href="/contact" className="text-gray-700 hover:text-[#0A66C2] font-medium transition-colors">Contact</a>
              <a href="/vraag-gratis-aanbod" className="text-[#F57C00] hover:text-[#E65100] font-medium transition-colors whitespace-nowrap">Vraag Gratis Aanbod</a>
            </div>
            <div className="flex items-center space-x-4">
              <a href="tel:097010224137" className="text-[#0A66C2] hover:text-[#F57C00] font-medium transition-colors">
                0970 102 241 37
              </a>
              <button className="bg-[#F57C00] text-white px-4 py-2 rounded-full font-medium hover:bg-[#E65100] transition-colors whitespace-nowrap cursor-pointer">
                Gratis check
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0A66C2] to-[#1976D2] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/29e4d617f1ecd576ffd60c4e0d2b97d5.jpg"
            alt="Digital Marketing"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="w-full">
              <h1 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
                Jouw groei online, <span className="text-[#F57C00]">zonder gedoe</span>
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-blue-100" style={{ fontFamily: '"Inter", sans-serif' }}>
                StarSales B.V. bouwt converterende websites en campagnes voor ZZP en MKB.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-[#F57C00] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#E65100] transition-colors whitespace-nowrap cursor-pointer">
                  Gratis marketingcheck
                </button>
                <button className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-[#0A66C2] transition-colors whitespace-nowrap cursor-pointer">
                  Voorbeeldcases bekijken
                </button>
              </div>
            </div>
            <div className="w-full">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20digital%20marketing%20dashboard%20showing%20website%20analytics%2C%20Google%20Ads%20performance%20metrics%2C%20social%20media%20campaign%20results%2C%20clean%20modern%20interface%20with%20blue%20and%20orange%20color%20scheme%2C%20data%20visualization%20charts%20and%20graphs%2C%20marketing%20ROI%20statistics&width=600&height=500&seq=hero-dashboard&orientation=landscape"
                alt="Marketing Dashboard"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Waarom kiezen voor StarSales?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" style={{ fontFamily: '"Inter", sans-serif' }}>
              Wij begrijpen de uitdagingen van ZZP'ers en kleine bedrijven en bieden oplossingen die echt werken.
            </p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 bg-[#0A66C2] text-white rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#F57C00] transition-colors">
                  <i className={`${benefit.icon} text-2xl`}></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-sm leading-tight" style={{ fontFamily: '"Inter", sans-serif' }}>
                  {benefit.title}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Onze diensten
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" style={{ fontFamily: '"Inter", sans-serif' }}>
              Van website tot complete online marketing strategie - wij zorgen voor jouw digitale groei.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 cursor-pointer group"
                onClick={() => setActiveService(index)}
              >
                <div className="w-12 h-12 bg-[#0A66C2] text-white rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#F57C00] transition-colors">
                  <i className={`${service.icon} text-xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: '"Poppins", sans-serif' }}>
                  {service.title}
                </h3>
                <p className="text-gray-600" style={{ fontFamily: '"Inter", sans-serif' }}>
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Ons proces
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" style={{ fontFamily: '"Inter", sans-serif' }}>
              In 4 stappen naar online succes
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#0A66C2] text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  {index + 1}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: '"Poppins", sans-serif' }}>
                  {step.title}
                </h3>
                <p className="text-gray-600" style={{ fontFamily: '"Inter", sans-serif' }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Onze pakketten
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" style={{ fontFamily: '"Inter", sans-serif' }}>
              Kies het pakket dat bij jouw bedrijf past
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <div 
                key={index} 
                className={`rounded-2xl p-8 border-2 transition-all duration-300 ${
                  plan.popular 
                    ? 'border-[#F57C00] bg-gradient-to-b from-[#F57C00]/5 to-white shadow-lg scale-105' 
                    : 'border-gray-200 bg-white hover:border-[#0A66C2] hover:shadow-lg'
                }`}
              >
                {plan.popular && (
                  <div className="bg-[#F57C00] text-white px-4 py-2 rounded-full text-sm font-semibold text-center mb-6">
                    Meest gekozen
                  </div>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: '"Poppins", sans-serif' }}>
                  {plan.name}
                </h3>
                <p className="text-gray-600 mb-4" style={{ fontFamily: '"Inter", sans-serif' }}>
                  {plan.description}
                </p>
                <div className="text-3xl font-bold text-[#0A66C2] mb-6">
                  {plan.price}
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-[#F57C00] mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button className={`w-full py-3 rounded-full font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  plan.popular
                    ? 'bg-[#F57C00] text-white hover:bg-[#E65100]'
                    : 'bg-[#0A66C2] text-white hover:bg-[#1976D2]'
                }`}
                onClick={() => window.REACT_APP_NAVIGATE('/contact')}
                >
                  Offerte aanvragen
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Wat onze klanten zeggen
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-[#F57C00] text-lg"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-6 italic" style={{ fontFamily: '"Inter", sans-serif' }}>
                "StarSales heeft onze online aanwezigheid volledig getransformeerd. Meer leads, betere conversie!"
              </p>
              <div className="flex items-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20business%20owner%20headshot%2C%20friendly%20smile%2C%20modern%20office%20background%2C%20high%20quality%20portrait%20photography%2C%20business%20attire%2C%20confident%20expression%2C%20middle-aged%20man%20with%20beard&width=60&height=60&seq=review-1&orientation=squarish"
                  alt="Klant"
                  className="w-12 h-12 rounded-full mr-4 object-cover"
                />
                <div>
                  <div className="font-semibold text-gray-900">Jan Bakker</div>
                  <div className="text-gray-600 text-sm">Eigenaar, Bakkerij Bakker</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-[#F57C00] text-lg"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-6 italic" style={{ fontFamily: '"Inter", sans-serif' }}>
                "Dankzij StarSales zijn onze Google Ads campagnes eindelijk winstgevend. Professioneel team!"
              </p>
              <div className="flex items-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20businesswoman%20headshot%2C%20confident%20smile%2C%20modern%20office%20background%2C%20high%20quality%20portrait%20photography%2C%20business%20attire%2C%20professional%20appearance%2C%20blonde%20hair&width=60&height=60&seq=review-2&orientation=squarish"
                  alt="Klant"
                  className="w-12 h-12 rounded-full mr-4 object-cover"
                />
                <div>
                  <div className="font-semibold text-gray-900">Lisa van der Berg</div>
                  <div className="text-gray-600 text-sm">Directeur, Wellness Studio Berg</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-[#F57C00] text-lg"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-6 italic" style={{ fontFamily: '"Inter", sans-serif' }}>
                "Onze nieuwe website ziet er fantastisch uit en we krijgen veel meer aanvragen binnen!"
              </p>
              <div className="flex items-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20business%20owner%20headshot%2C%20friendly%20smile%2C%20modern%20office%20background%2C%20high%20quality%20portrait%20photography%2C%20business%20attire%2C%20confident%20expression%2C%20young%20entrepreneur%20with%20glasses&width=60&height=60&seq=review-3&orientation=squarish"
                  alt="Klant"
                  className="w-12 h-12 rounded-full mr-4 object-cover"
                />
                <div>
                  <div className="font-semibold text-gray-900">Mark Jansen</div>
                  <div className="text-gray-600 text-sm">Oprichter, TechSolutions</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Veelgestelde vragen
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-2xl overflow-hidden">
                <button
                  className="w-full px-8 py-6 text-left flex justify-between items-center hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-gray-900" style={{ fontFamily: '"Inter", sans-serif' }}>
                    {faq.question}
                  </span>
                  <i className={`ri-arrow-down-s-line text-2xl text-gray-400 transition-transform ${
                    openFaq === index ? 'rotate-180' : ''
                  }`}></i>
                </button>
                {openFaq === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-600" style={{ fontFamily: '"Inter", sans-serif' }}>
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#0A66C2] to-[#1976D2] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
            Klaar om te groeien?
          </h2>
          <p className="text-xl mb-8 text-blue-100" style={{ fontFamily: '"Inter", sans-serif' }}>
            Vraag een gratis marketingcheck aan en ontdek hoe wij jouw bedrijf online kunnen laten groeien.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-[#F57C00] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#E65100] transition-colors whitespace-nowrap cursor-pointer">
              Gratis marketingcheck
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-[#0A66C2] transition-colors whitespace-nowrap cursor-pointer">
              Bel ons: 0970 102 241 37
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center mb-6">
                <span className="text-3xl font-bold text-[#0A66C2]" style={{ fontFamily: '"Pacifico", serif' }}>
                  StarSales
                </span>
              </div>
              <p className="text-gray-300 mb-6 max-w-md" style={{ fontFamily: '"Inter", sans-serif' }}>
                StarSales B.V. helpt ZZP en kleine bedrijven groeien met websites, Google Ads, social media advertenties en meer.
              </p>
              <div className="space-y-2 text-gray-300">
                <p>Mgr. Cornelis Veermanlaan 63</p>
                <p>1131 KE Volendam</p>
                <p>KvK: 97377562</p>
                <p>Tel: 0970 102 241 37</p>
                <p>E-mail: info@starsales.nl</p>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-4" style={{ fontFamily: '"Poppins", sans-serif' }}>
                Diensten
              </h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#" className="hover:text-[#F57C00] transition-colors">Websites</a></li>
                <li><a href="#" className="hover:text-[#F57C00] transition-colors">Google Ads</a></li>
                <li><a href="#" className="hover:text-[#F57C00] transition-colors">Social Media Ads</a></li>
                <li><a href="#" className="hover:text-[#F57C00] transition-colors">SEO</a></li>
                <li><a href="#" className="hover:text-[#F57C00] transition-colors">Hosting</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4" style={{ fontFamily: '"Poppins", sans-serif' }}>
                Links
              </h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="/privacy" className="hover:text-[#F57C00] transition-colors">Privacy</a></li>
                <li><a href="/cookies" className="hover:text-[#F57C00] transition-colors">Cookies</a></li>
                <li><a href="/voorwaarden" className="hover:text-[#F57C00] transition-colors">Voorwaarden</a></li>
              </ul>
            </div>
          </div>
          
          {/* Google Maps */}
          <div className="mb-12">
            <h3 className="font-semibold mb-4" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Bezoek ons kantoor
            </h3>
            <div className="rounded-2xl overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2426.123456789!2d5.0123456!3d52.4987654!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sMgr.%20Cornelis%20Veermanlaan%2063%2C%201131%20KE%20Volendam!5e0!3m2!1snl!2snl!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2024 StarSales B.V. Alle rechten voorbehouden.
            </p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-[#F57C00] transition-colors">
                <i className="ri-facebook-fill text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-[#F57C00] transition-colors">
                <i className="ri-instagram-line text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-[#F57C00] transition-colors">
                <i className="ri-linkedin-fill text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
